<script setup lang="ts">
import { ref, inject, provide } from "vue";
import { useRouter } from "vue-router";
import { ChevronsRight } from "lucide-vue-next";
import type { NavigationItem } from "./type";

const props = defineProps<{
  items: NavigationItem[];
  accordion?: boolean;
  parentTextClass?: string; // 父级传入的样式
  parentTextStyle?: Record<string, any>;
}>();

const emit = defineEmits(["navigate"]);

const router = useRouter();

const EXPANDED_KEYS = Symbol("expandedKeys");
const parentExpanded = inject(EXPANDED_KEYS, null);
const expandedKeys = parentExpanded || ref(new Set<string>());

provide(EXPANDED_KEYS, expandedKeys);

const isExpanded = (item: NavigationItem) => expandedKeys.value.has(item.id);

/** 获取文本样式类 - 优先使用自身，没有则继承父级 */
const getTextClass = (item: NavigationItem): string => {
  // 优先级：自身 textClass > 父级 parentTextClass > 默认 text-black
  return item.textClass || props.parentTextClass || "text-black";
};

/** 获取内联样式 */
const getTextStyle = (item: NavigationItem): Record<string, any> => {
  return {
    ...props.parentTextStyle,
    ...item.textStyle,
  };
};

const handleClick = (item: NavigationItem) => {
  if (item.children?.length) {
    if (props.accordion) {
      props.items.forEach((i) => {
        if (i.id !== item.id) expandedKeys.value.delete(i.id);
      });
    }

    if (expandedKeys.value.has(item.id)) {
      expandedKeys.value.delete(item.id);
    } else {
      expandedKeys.value.add(item.id);
    }

    return;
  }

  if (item.href) {
    router.push(item.href);
    emit("navigate");
  }
};
</script>

<template>
  <ul class="space-y-1">
    <li v-for="item in items" :key="item.id">
      <div
        class="flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100 cursor-pointer"
        @click="handleClick(item)"
      >
        <div class="w-5 flex justify-center">
          <ChevronsRight
            v-if="item.children"
            class="w-4 h-4 transition-transform"
            :class="{ 'rotate-90': isExpanded(item) }"
          />
        </div>

        <!-- 文本 - 自动继承样式 -->
        <div
          class="flex-1"
          :class="getTextClass(item)"
          :style="getTextStyle(item)"
        >
          {{ item.label }}
        </div>
      </div>

      <!-- 子菜单 - 传递当前样式作为父级样式 -->
      <transition name="expand">
        <div v-if="item.children && isExpanded(item)" class="ml-4">
          <MobileMenuList
            :items="item.children"
            :accordion="props.accordion"
            :parent-text-class="getTextClass(item)"
            :parent-text-style="getTextStyle(item)"
            @navigate="$emit('navigate')"
          />
        </div>
      </transition>
    </li>
  </ul>
</template>

<style scoped>
.expand-enter-active,
.expand-leave-active {
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 500px; /* 足够大的值，根据实际子菜单深度调整 */
}
</style>
